import _ from "lodash";

import { DOMINIO_DASHBOARD_REFERENCE_IDS } from "../../../shared/constants/dominio-dashboards.constants";
import { SourcesService } from "../SourcesService/SourcesService";

export class DashboardsViewService {
    constructor({
        AvailableHttpGateway,
        CompanyDataAvailableHttpGateway,
        DashboardAvailableHttpGateway,
        ScenarioHttpGateway
    } = {}) {
        this.AvailableHttpGateway = AvailableHttpGateway;
        this.CompanyDataAvailableHttpGateway = CompanyDataAvailableHttpGateway;
        this.DashboardAvailableHttpGateway = DashboardAvailableHttpGateway;
        this.ScenarioHttpGateway = ScenarioHttpGateway;
    }

    _getValidatePersonnelSource({
        source,
        dashboardId,
        companyId,
        currentSource
    } = {}) {
        let _source = source;

        const otherSources = {
            personnel: "eSocial",
            eSocial: "personnel"
        };

        if (!dashboardId) {
            const available = this.AvailableHttpGateway.getAllAvailable(
                companyId
            );
            const sourcesAvailable = this.CompanyDataAvailableHttpGateway.getSourcesAvailable(
                available
            );

            if (sourcesAvailable.includes(source)) _source = source;
            else if (!sourcesAvailable.includes(_source))
                _source = otherSources[_source];

            return _source;
        }

        return this.getSourceByDashboard({
            dashboardId,
            companyId,
            currentSource
        });
    }

    _getValidateTaxSource({
        source,
        dashboardId,
        companyId,
        currentSource
    } = {}) {
        let _source = source;

        const otherSources = {
            tax: "simplesNacional",
            simplesNacional: "tax"
        };

        if (!dashboardId) {
            const available = this.AvailableHttpGateway.getAllAvailable(
                companyId
            );
            const sourcesAvailable = this.CompanyDataAvailableHttpGateway.getSourcesAvailable(
                available
            );

            if (sourcesAvailable.includes(source)) _source = source;
            else if (!sourcesAvailable.includes(_source))
                _source = otherSources[_source];

            return _source;
        }

        return this.getSourceByDashboard({
            dashboardId,
            companyId,
            currentSource
        });
    }

    getValidateSource({ source, dashboardId, companyId, currentSource } = {}) {
        if (source == "tax")
            return this._getValidateTaxSource({
                source,
                dashboardId,
                companyId,
                currentSource
            });

        if (source === "personnel") {
            return this._getValidatePersonnelSource({
                source,
                dashboardId,
                companyId,
                currentSource
            });
        }

        return source;
    }

    getSourceByDashboard({ dashboardId, companyId, currentSource } = {}) {
        const dashboard = this.DashboardAvailableHttpGateway.getByDashboardId({
            companyId: companyId,
            dashboardId: dashboardId
        });

        return dashboard.source || currentSource;
    }

    getSourcesAvailableFixedBySource({ companyId } = {}) {
        const available = this.AvailableHttpGateway.getAllAvailable(companyId);

        const sourcesAvailable = this.CompanyDataAvailableHttpGateway.getSourcesAvailable(
            available
        );

        const sourcesMap = [
            {
                indexOfSource: "simplesNacional",
                sourceReplace: "tax"
            },
            {
                indexOfSource: "eSocial",
                sourceReplace: "personnel"
            }
        ];

        for (const source of sourcesMap) {
            const hasIndexOfSource = sourcesAvailable.indexOf(
                source.indexOfSource
            );

            if (hasIndexOfSource > -1)
                sourcesAvailable[hasIndexOfSource] = source.sourceReplace;
        }

        return sourcesAvailable;
    }

    getDashboardSourcesSelector({ sources } = {}) {
        if (typeof sources != "string") {
            return {
                $in: sources
            };
        }
        const sourcesService = new SourcesService();
        const sourcesMap = {
            tax: {
                $in: sourcesService.getSourceWithSubSourceArray("tax")
            },
            simplesNacional: {
                $in: sourcesService.getSourceWithSubSourceArray("tax")
            },
            personnel: {
                $in: sourcesService.getSourceWithSubSourceArray("personnel")
            },
            eSocial: {
                $in: sourcesService.getSourceWithSubSourceArray("personnel")
            }
        };
        if (sourcesMap[sources]) return sourcesMap[sources];
        return sources;
    }

    /**
     * Fiscal e PGDAS (e Pessoal e eSocial) sao o mesmo modulo: a ordem definida na
     * customizacao vale para o grupo inteiro, entao o primeiro dashboard e o de menor
     * posicao entre as duas fontes.
     *
     * Empresa com a integracao Dominio ativa na fonte do modulo abre no primeiro dashboard
     * do Dominio, mesmo que PGDAS/eSocial estejam antes na ordem: e de la que vem o dado
     * dela. Sem dashboard do Dominio visivel, vale a ordem. Tambem vale a ordem quando a fonte
     * principal ainda nao tem nenhuma data (`sourcesAvailable`): o dashboard do Dominio abriria
     * vazio, com o periodo emprestado do PGDAS/eSocial, no lugar do dashboard que tem dado.
     */
    getFirstDashboardIdBySourceOrder({
        dashboards = [],
        source,
        company,
        sourcesAvailable = []
    } = {}) {
        const sourceGroup = new SourcesService().getSourceWithSubSourceArray(
            source,
            true
        );

        const ordered = _.sortBy(
            dashboards.filter(
                (dashboard) =>
                    !dashboard.hidden &&
                    sourceGroup.some((sourceItem) =>
                        dashboard.sources?.includes(sourceItem)
                    )
            ),
            "position"
        );

        const mainSource = new SourcesService().getMainSource({ source });
        const prefersDominio =
            sourcesAvailable.includes(mainSource) &&
            this.hasDominioIntegration({ company, source });
        const dominioDashboard = prefersDominio
            ? ordered.find((dashboard) =>
                  DOMINIO_DASHBOARD_REFERENCE_IDS.includes(
                      dashboard.metadata?.referenceId
                  )
              )
            : null;

        return _.get(dominioDashboard || _.head(ordered), "_id", null);
    }

    hasDominioIntegration({ company, source } = {}) {
        const mainSource = new SourcesService().getMainSource({ source });

        return Boolean(
            company?.relation?.dominio?.sourcesIntegration?.some(
                (integration) =>
                    integration.source === mainSource && integration.active
            )
        );
    }

    reconfigSections(dashboard) {
        // return dashboard;
        if (!dashboard || !dashboard.sections) return null;
        const newSections = [];
        const isEven = (n) => n % 2 === 0;
        dashboard.sections.forEach((section) => {
            const totalVisualizations = section.visualizationsIds.length;
            if (totalVisualizations > 2) {
                if (isEven(totalVisualizations)) {
                    newSections.push({
                        height: 50,
                        template: "1,1",
                        visualizationsIds: _.take(section.visualizationsIds, 2),
                        visualizations: _.take(section.visualizations, 2)
                    });
                    newSections.push({
                        height: 50,
                        template: "1,1",
                        visualizationsIds: _.takeRight(
                            section.visualizationsIds,
                            2
                        ),
                        visualizations: _.takeRight(section.visualizations, 2)
                    });
                } else {
                    newSections.push({
                        height: 50,
                        template: "1,1",
                        visualizationsIds: _.take(section.visualizationsIds, 2),
                        visualizations: _.take(section.visualizations, 2)
                    });
                    newSections.push({
                        height: 50,
                        template: "1",
                        visualizationsIds: [_.last(section.visualizationsIds)],
                        visualizations: [_.last(section.visualizations)]
                    });
                }
            } else {
                // if (section.height <= 50) section.height = 66;
                // if (section.height === 75) section.height = 100;
                newSections.push(section);
            }
        });
        dashboard.projectionSection = newSections;
        return dashboard;
    }

    invertDatesByComparation({ period, comparation }) {
        if (period.scenario._id != comparation.scenario._id)
            return {
                periodDate: period.date,
                comparationDate: comparation.date
            };
        if (comparation.date > period.date) {
            return {
                periodDate: comparation.date,
                comparationDate: period.date,
                isAbove: true
            };
        }

        return { periodDate: period.date, comparationDate: comparation.date };
    }

    /**
     * Parâmetros do `$state.go` para a tela de Dados, a partir da migalha do dashboard.
     *
     * A fonte vem junto de propósito: sem ela o `data-redirect` cai no `_getFirstSource()`, a
     * primeira fonte CONTRATADA — que quase nunca é a que a pessoa está analisando. Voltar de
     * um dashboard Financeiro para os dados Contábeis é trocar de assunto no meio do caminho.
     *
     * `getMainSource` colapsa `simplesNacional` → `tax` e `eSocial` → `personnel`, que é o
     * vocabulário que o `data-redirect` conhece em `sourcesContracts`; mandar a subfonte crua
     * cairia no mesmo fallback. Sem fonte na tela, devolve `""` e o comportamento é o de antes.
     */
    getDataRedirectParams({ accountingId, companyId, source } = {}) {
        return {
            accountingId,
            id: companyId,
            source: new SourcesService().getMainSource({ source })
        };
    }

    getDashboardParams({ companyId, view, scenarioId, year, source }) {
        const views = {
            mensal: "monthly",
            trimestral: "quarterly",
            semestral: "semester",
            anual: "yearly",
            monthly: "monthly",
            quarterly: "quarterly",
            semester: "semester",
            yearly: "yearly"
        };

        const date = year.split("-");
        year = date[0];

        const scenarios = {
            PROGRESSAO_LINEAR: () => {
                const lastMonth = this.AvailableHttpGateway.getLastMonth(
                    companyId,
                    year,
                    source,
                    "ORIGINAL",
                    {
                        projection: "PROGRESSAO_LINEAR"
                    }
                );

                return {
                    scenario: "ORIGINAL",
                    scenarioCompare: "ORIGINAL",
                    date: `${year}-${lastMonth}`,
                    period: views[view],
                    comparationPeriod: views[view],
                    projection: "PROGRESSAO_LINEAR",
                    budget: false
                };
            },
            ORIGINAL: () => {
                const lastMonth = this.AvailableHttpGateway.getLastMonth(
                    companyId,
                    year,
                    source,
                    "ORIGINAL"
                );
                return {
                    scenario: "ORIGINAL",
                    scenarioCompare: "ORIGINAL",
                    date: `${year}-${lastMonth || date[1]}`,
                    period: views[view],
                    comparationPeriod: views[view],
                    projection: false,
                    budget: false
                };
            },
            budget: () => {
                const lastMonth = this.AvailableHttpGateway.getLastMonth(
                    companyId,
                    year,
                    source,
                    scenarioId
                );
                return {
                    scenario: "ORIGINAL",
                    scenarioCompare: "ORIGINAL",
                    date: `${year}-${lastMonth}`,
                    period: views[view],
                    comparationPeriod: views[view],
                    projection: false,
                    budget: scenarioId
                };
            },
            projection: () => {
                const lastMonth = this.AvailableHttpGateway.getLastMonth(
                    companyId,
                    year,
                    source,
                    scenarioId
                );
                return {
                    scenario: "ORIGINAL",
                    scenarioCompare: "ORIGINAL",
                    date: `${year}-${lastMonth}`,
                    period: views[view],
                    comparationPeriod: views[view],
                    projection: scenarioId,
                    budget: false
                };
            },
            standard: () => {
                const lastMonth = this.AvailableHttpGateway.getLastMonth(
                    companyId,
                    year,
                    source,
                    scenarioId
                );
                return {
                    scenario: scenarioId,
                    scenarioCompare: "ORIGINAL",
                    date: `${year}-${lastMonth}`,
                    period: views[view],
                    comparationPeriod: views[view],
                    projection: false,
                    budget: false
                };
            }
        };
        let params = {};

        if (scenarios[scenarioId]) {
            params = scenarios[scenarioId]();
        } else {
            const scenario = this.ScenarioHttpGateway.findOneCollection({
                _id: scenarioId
            });
            params = scenarios[scenario.type]();
        }
        return params;
    }
}
